// MIT License - Copyright (c) 2026 Doug McNall - See .kiro/steering/general/development-standards.md for full license

/**
 * MiniChart Component
 * 
 * Lightweight sparkline chart for showing data trends
 * Perfect for dashboard stats and compact visualizations
 */

import React from 'react';
import './MiniChart.css';

const MiniChart = ({ 
  data = [], 
  width = 100, 
  height = 30, 
  color = '#00ffff',
  fillColor = 'rgba(0, 255, 255, 0.1)',
  strokeWidth = 2,
  showDots = false,
  animate = true
}) => {
  if (!data || data.length === 0) {
    return <div className="mini-chart-empty" style={{ width, height }} />;
  }

  // Calculate min/max for scaling
  const min = Math.min(...data);
  const max = Math.max(...data);
  const range = max - min || 1; // Avoid division by zero

  // Generate SVG path
  const points = data.map((value, index) => {
    const x = (index / (data.length - 1)) * width;
    const y = height - ((value - min) / range) * height;
    return { x, y, value };
  });

  // Create line path
  const linePath = points
    .map((point, index) => `${index === 0 ? 'M' : 'L'} ${point.x} ${point.y}`)
    .join(' ');

  // Create area path (for fill)
  const areaPath = `${linePath} L ${width} ${height} L 0 ${height} Z`;

  return (
    <svg 
      className={`mini-chart ${animate ? 'animate' : ''}`}
      width={width} 
      height={height}
      viewBox={`0 0 ${width} ${height}`}
    >
      {/* Fill area */}
      <path
        d={areaPath}
        fill={fillColor}
        className="mini-chart-area"
      />
      
      {/* Line */}
      <path
        d={linePath}
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className="mini-chart-line"
      />
      
      {/* Dots at data points */}
      {showDots && points.map((point, index) => (
        <circle
          key={index}
          cx={point.x}
          cy={point.y}
          r={2}
          fill={color}
          className="mini-chart-dot"
        />
      ))}
    </svg>
  );
};

export default MiniChart;
